import { ReactNode, useState, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  AppBar, Box, CssBaseline, Divider, Drawer, IconButton,
  List, ListItem, ListItemButton, ListItemIcon, ListItemText,
  Toolbar, Tooltip, Typography
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PersonIcon from '@mui/icons-material/Person';
import WorkIcon from '@mui/icons-material/Work';
import LinkIcon from '@mui/icons-material/Link';
import AdminPanelSettingsIcon from '@mui/icons-material/AdminPanelSettings';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import BusinessIcon from '@mui/icons-material/Business';
import StarBorderIcon from '@mui/icons-material/StarBorder';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import UserProfile from '../auth/UserProfile';
import { usePermissions } from '../../hooks/usePermissions';
import { usePioDirectorySync } from '../../hooks/usePioDirectorySync';
import { AnnounceProvider } from '../../hooks/useAnnounce';
import { Permission } from '../../types/auth';


const drawerWidth = 240;
const collapsedWidth = 64;

interface MainLayoutProps {
  children: ReactNode;
}

export default function MainLayout({ children }: MainLayoutProps) {
  usePioDirectorySync(); // peuple l'annuaire PIO au login (silencieux, best-effort)
  const [mobileOpen, setMobileOpen] = useState(false);
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try { return localStorage.getItem('nav.collapsed') === '1'; } catch { return false; }
  });
  const navigate = useNavigate();
  const location = useLocation();

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const toggleCollapsed = () => {
    setCollapsed((c) => {
      try { localStorage.setItem('nav.collapsed', c ? '0' : '1'); } catch { /* ignore */ }
      return !c;
    });
  };

  // Largeur de la nav desktop : repliée (icônes seules) ou pleine.
  const navWidth = collapsed ? collapsedWidth : drawerWidth;

  const { hasPermission } = usePermissions();

  const menuItems = useMemo(() => {
    const items = [
      { text: 'Dashboard', path: '/', icon: <DashboardIcon />, disabled: false },
      { text: 'Candidats', path: '/candidates', icon: <PersonIcon />, disabled: false },
      { text: 'Favoris', path: '/favorites', icon: <StarBorderIcon />, disabled: false },
    ];

    if (hasPermission(Permission.CLIENTS_VIEW)) {
      items.push({ text: 'Clients', path: '/clients', icon: <BusinessIcon />, disabled: false });
      items.push({ text: 'Entreprises', path: '/companies', icon: <BusinessIcon />, disabled: false });
    }


    if (hasPermission(Permission.CRM_VIEW)) {
      items.push({ text: 'CRM', path: '/crm', icon: <AccountTreeIcon />, disabled: false });
      items.push({ text: 'CRM 360°', path: '/crm/comptes', icon: <BusinessIcon />, disabled: false });
    }

    items.push({ text: 'TurnoverIT', path: '/jobs-turnover', icon: <TrendingUpIcon />, disabled: false });
    items.push({ text: 'Opportunités', path: '/jobs', icon: <WorkIcon />, disabled: true });
    items.push({ text: 'Matchs', path: '/matches', icon: <LinkIcon />, disabled: true });

    if (hasPermission(Permission.REFERENTIEL_VIEW)) {
      items.push({ text: 'Référentiel', path: '/referentiel-competences', icon: <LocalOfferIcon />, disabled: false });
    }

    if (hasPermission(Permission.ADMIN_ES_MANAGEMENT)) {
      items.push({ text: 'Administration', path: '/admin', icon: <AdminPanelSettingsIcon />, disabled: false });
      // Centre de pilotage IA (page AIAdmin) : endpoints backend gated ADMIN_ES_MANAGEMENT → menu admin.
      items.push({ text: 'Admin IA', path: '/ai', icon: <SmartToyIcon />, disabled: false });
    }

    return items;
  }, [hasPermission]);

  // `mini` = mode replié (icônes seules + tooltips). N'affecte que la nav desktop.
  const drawerContent = (mini: boolean) => (
    <div style={{ backgroundColor: '#061535', height: '100%' }}>
      <Toolbar sx={{ py: 2 }}>
        <Typography variant="h5" noWrap component="div" align="center" sx={{
          flexGrow: 1,
          fontWeight: 'bold',
          fontSize: mini ? '1.25rem' : '1.5rem'
        }}>
          <span style={{ color: '#FFFFFF' }}>{mini ? 'A' : 'Agora'}</span>
          <span style={{ color: '#EF8F1C' }}>{mini ? 'S' : 'Search'}</span>
        </Typography>
      </Toolbar>
      <Divider sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', margin: '16px 0' }} />
      <List>
        {menuItems.map((item) => {
          const selected = item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path);
          const button = (
            <ListItemButton
              selected={selected}
              onClick={() => !item.disabled && navigate(item.path)}
              disabled={item.disabled}
              sx={{
                color: item.disabled ? 'rgba(255, 255, 255, .3)' : '#FFFFFF',
                borderRadius: '12px',
                margin: '4px 8px',
                justifyContent: mini ? 'center' : 'flex-start',
                '&:hover': item.disabled ? {} : {
                  backgroundColor: 'rgba(239, 143, 28, .2)',
                  transform: 'translateX(4px)',
                  transition: 'all 0.3s ease'
                },
                '&.Mui-selected': {
                  backgroundColor: '#EF8F1C',
                  '&:hover': {
                    backgroundColor: '#EF8F1C',
                  }
                },
                cursor: item.disabled ? 'not-allowed' : 'pointer',
                transition: 'all 0.3s ease'
              }}
            >
              <ListItemIcon sx={{
                color: item.disabled ? 'rgba(255, 255, 255, .3)' : '#FFFFFF',
                minWidth: mini ? 0 : '40px',
                justifyContent: 'center'
              }}>
                {item.icon}
              </ListItemIcon>
              {!mini && <ListItemText primary={item.text} />}
            </ListItemButton>
          );
          return (
            <ListItem key={item.text} disablePadding>
              {mini
                ? <Tooltip title={item.text} placement="right"><Box sx={{ width: '100%' }}>{button}</Box></Tooltip>
                : button}
            </ListItem>
          );
        })}
      </List>
    </div>
  );

  return (
    <Box sx={{ display: 'flex' }}>
      <CssBaseline />
      <AppBar
        position="fixed"
        sx={{
          width: { sm: `calc(100% - ${navWidth}px)` },
          ml: { sm: `${navWidth}px` },
          transition: 'width 0.2s ease, margin 0.2s ease',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="ouvrir le menu"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { sm: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          <IconButton
            color="inherit"
            aria-label={collapsed ? 'déplier le menu' : 'replier le menu'}
            edge="start"
            onClick={toggleCollapsed}
            sx={{ mr: 2, display: { xs: 'none', sm: 'inline-flex' } }}
          >
            {collapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
          </IconButton>
          <Typography variant="h6" noWrap component="div" sx={{ flexGrow: 1 }}>
            {menuItems.find(item => item.path === '/' ? location.pathname === '/' : location.pathname.startsWith(item.path))?.text || 'AgoraSearch'}
          </Typography>
          <UserProfile />
        </Toolbar>
      </AppBar>
      <Box
        component="nav"
        sx={{ width: { sm: navWidth }, flexShrink: { sm: 0 } }}
        aria-label="menu items"
      >
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile.
          }}
          sx={{
            display: { xs: 'block', sm: 'none' },
            '& .MuiDrawer-paper': { boxSizing: 'border-box', width: drawerWidth },
          }}
        >
          {drawerContent(false)}
        </Drawer>
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', sm: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: navWidth,
              overflowX: 'hidden',
              transition: 'width 0.2s ease',
            },
          }}
          open
        >
          {drawerContent(collapsed)}
        </Drawer>
      </Box>
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { sm: `calc(100% - ${navWidth}px)` },
          transition: 'width 0.2s ease',
        }}
      >
        <Toolbar />
        {/* Provider de la région live ARIA (WCAG 4.1.3) monté une seule fois,
            autour du contenu routé. Toute page peut appeler useAnnounce(). */}
        <AnnounceProvider>
          {children}
        </AnnounceProvider>
      </Box>
    </Box>
  );
}
